import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Lock } from "lucide-react";
import { apiErrorMessage } from "../lib/api-error.js";
import { useLoginMutation } from "../store/api.js";
import { credentialsReceived } from "../store/auth.slice.js";
import { useAppDispatch, useAppSelector } from "../store/index.js";
import { Brand, YkkbiLogoImage } from "../components/Brand.js";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { accessToken, user } = useAppSelector((state) => state.auth);
  const [login, { isLoading, error }] = useLoginMutation();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [remember, setRemember] = useState(false);

  if (accessToken && user) {
    return <Navigate to={user.mustChangePassword ? "/change-password" : "/"} replace />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const result = await login({ username, password });
    if ("error" in result) return;
    dispatch(credentialsReceived(result.data));
    navigate(result.data.user.mustChangePassword ? "/change-password" : "/", { replace: true });
  }

  return (
    <div className="flex min-h-svh flex-col justify-between bg-[#F6F7F9] bg-dotgrid">
      {/* Top Header */}
      <header className="flex items-center justify-between px-6 py-4 sm:px-10">
        <Brand />
        <div className="flex items-center gap-4 text-[13px]">
          <a
            href="https://github.com/vantuan88291/react-native-ota-hot-update"
            target="_blank"
            rel="noreferrer"
            className="text-[#667085] transition-colors hover:text-[#101828]"
          >
            Butuh bantuan?
          </a>
        </div>
      </header>

      {/* Main Login Card */}
      <main className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-[420px] rounded-2xl border border-[#EAECF0] bg-white p-7 sm:p-9 shadow-[0_20px_40px_-15px_rgba(16,24,40,0.08)]">
          {/* Official Logo */}
          <div className="mb-5">
            <YkkbiLogoImage size="lg" />
          </div>

          <h1 className="text-[24px] font-bold tracking-tight text-[#101828]">
            Masuk ke Konsol OTA
          </h1>
          <p className="mt-1 text-[13.5px] text-[#667085]">
            Masukkan akun administrator atau rilis untuk melanjutkan.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-[13px] font-medium text-[#344054]">
                Email atau nama pengguna
              </Label>
              <Input
                id="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="mis. admin.ota"
                autoComplete="username"
                autoFocus
                required
                className="h-10 text-[13.5px] border-[#D0D5DD] rounded-lg focus-visible:ring-[#2563EB]"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[13px] font-medium text-[#344054]">
                  Kata sandi
                </Label>
                <a
                  href="#forgot"
                  onClick={(e) => {
                    e.preventDefault();
                    alert("Silakan hubungi administrator sistem Bank Indonesia untuk reset kata sandi.");
                  }}
                  className="text-[12px] font-medium text-[#2563EB] hover:underline"
                >
                  Lupa sandi?
                </a>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={reveal ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  className="h-10 pr-10 text-[13.5px] border-[#D0D5DD] rounded-lg focus-visible:ring-[#2563EB]"
                  required
                />
                <button
                  type="button"
                  onClick={() => setReveal((value) => !value)}
                  className="absolute top-1/2 right-2.5 -translate-y-1/2 rounded p-1 text-[#98A2B3] transition-colors hover:text-[#344054]"
                  aria-label={reveal ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
                >
                  {reveal ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1 text-[13px] text-[#344054]">
              <Checkbox
                id="remember"
                checked={remember}
                onCheckedChange={(value) => setRemember(value === true)}
              />
              <label htmlFor="remember" className="cursor-pointer select-none">
                Ingat perangkat 30 hari
              </label>
            </div>

            {error && (
              <Alert variant="destructive" className="border-[#FECDCA] bg-[#FEF3F2] text-[#B42318] py-2">
                <AlertDescription className="text-xs">{apiErrorMessage(error)}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="mt-2 h-11 w-full rounded-lg bg-[#0F1B3D] hover:bg-[#1D2939] text-white text-[14px] font-medium transition-colors shadow-xs"
              disabled={isLoading}
            >
              {isLoading ? "Memeriksa…" : "Masuk ke konsol"}
              <ArrowRight className="size-4 ml-1.5" />
            </Button>
          </form>

          {/* Security note */}
          <div className="mt-6 flex items-start gap-2 rounded-lg bg-[#F6F7F9] p-3 text-[12px] text-[#667085] border border-[#EAECF0]">
            <Lock className="mt-0.5 size-3.5 shrink-0 text-[#98A2B3]" />
            <span>Koneksi aman ke ota.ykkbi.or.id · Akses terbatas untuk personel terotorisasi Bank Indonesia</span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-5 text-center text-[12px] text-[#667085] sm:px-10">
        v2.4.1 · © 2026 Yayasan Kesejahteraan Karyawan Bank Indonesia
      </footer>
    </div>
  );
}
